import { ApolloError } from '@apollo/client';
import {
  Box,
  Button,
  LinearProgress,
  Table,
  TablePagination,
  Toolbar,
  Typography,
} from '@material-ui/core';
import BlockIcon from '@mui/icons-material/Block';
import CheckIcon from '@mui/icons-material/Check';
import * as Sentry from '@sentry/react';
import React, { useState } from 'react';
import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { showErrorAction } from 'containers/Notification/actions';
import { Tabs } from 'containers/VolunteerRewardsListPage/Tabs';
import { fetchAllLocationsAction } from 'entities/location/actions';
import epics from 'entities/location/epics';
import reducer from 'entities/location/reducer';
import { selectOrganization, selectUser } from 'entities/user/selectors';
import VolunteerTime from 'entities/volunteerTime/model';
import {
  SortOrder,
  VolunteerTimeApproval,
  VolunteerTimesWithPaginationDocument,
  useUpdateVolunteerTimesMutation,
  useVolunteerTimesWithPaginationQuery,
} from 'generated/graphql';
import PageHeader from 'retired/PageHeader';
import { TableBox } from 'retired/TableBox';
import TablePaginationActions from 'retired/TablePaginationActions';
import { primaryBlue, red } from 'utils/color-styles';
import { Features } from 'utils/features';
import { useInjectEpics } from 'utils/injectEpics';
import { useInjectReducer } from 'utils/injectReducer';
import { Privileges } from 'utils/privileges';

import { VolunteerTimeDetails } from './VolunteerTimeDetails';
import { VolunteerTimesTableBody } from './VolunteerTimesTableBody';
import { VolunteerTimesTableHead } from './VolunteerTimesTableHead';

const styles = {
  approveBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: primaryBlue,
    borderRadius: 5,
    padding: '8px 20px',
    marginTop: 16,
  },
  tableHeader: {
    justifyContent: 'flex-start',
  },
  tableHeaderControls: {
    marginLeft: 'auto',
  },
  tableHeaderControl: {
    marginLeft: '16px',
  },
  tableHeaderControlIcon: { marginLeft: 5, alignSelf: 'flex-start' },
};

const VolunteerTimesListPage: React.FC<{}> = () => {
  const history = useHistory();
  useInjectReducer({ key: 'volunteerTimesListPage', reducer });
  useInjectEpics({ key: 'volunteerTimesListPage', epics });

  const dispatch = useDispatch();

  const user = useSelector(selectUser);

  dispatch(fetchAllLocationsAction());

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [volunteerTimeDetails, setVolunteerTimeDetails] = useState<VolunteerTime>();
  const [statusUpdating, setStatusUpdating] = useState(new Map<string, boolean>());

  if (user && !user.hasPrivilege(Privileges.VOLUNTEERTIME)) {
    history.replace('/');
  }

  const organization = useSelector(selectOrganization);

  const query = {
    approval: { equals: VolunteerTimeApproval.Pending },
  };

  if (organization?.isCompany()) {
    query.companyId = { equals: organization.id };
  } else {
    query.nonprofitId = { equals: organization.id };
  }
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const queryVariables = {
    where: query,
    skip: page * rowsPerPage,
    take: rowsPerPage,
    orderBy: { createdAt: SortOrder.Asc },
  };

  const {
    data,
    error: volunteerTimesError,
    loading: volunteerTimesLoading,
  } = useVolunteerTimesWithPaginationQuery({
    variables: queryVariables,
    // We unfortunately need to disable caching, because it is tricky when users approve
    // different volunteer times on different pages. In this scenario we need to refetch those multiple specific pages.
    // Instead we decided to go for the bulletproof option, to always refetch and start from page 1
    fetchPolicy: 'network-only',
  });

  const handleUpdateError = (error?: ApolloError) => {
    setStatusUpdating(new Map());
    dispatch(showErrorAction('Failed to update Volunteer Hour'));
    Sentry.captureException(error, { level: 'error' });
  };

  // We need this only for the first page, because in other cases setPage(0); triggers a rerender
  // which results in a refetch
  const refetchPolicy =
    page === 0
      ? {
          refetchQueries: [
            {
              query: VolunteerTimesWithPaginationDocument,
              variables: queryVariables,
            },
          ],
        }
      : {};

  const [
    updateVolunteerTimes,
    { loading: updateVolunteerTimeLoading, error: updateVolunteerTimeError },
  ] = useUpdateVolunteerTimesMutation({
    onCompleted: () => {
      setPage(0);
      setStatusUpdating(new Map());
      setSelectedItems([]);
    },
    onError: () => {
      handleUpdateError(updateVolunteerTimeError);
    },
    ...refetchPolicy,
    notifyOnNetworkStatusChange: true,
  });

  const anyStatusUpdating =
    Boolean(Object.values(Object.fromEntries(statusUpdating)).filter(Boolean).length) ||
    volunteerTimesLoading ||
    updateVolunteerTimeLoading;

  const volunteerTimes = data?.volunteerTimesWithPagination?.items;
  const volunteerTimesMetaData = data?.volunteerTimesWithPagination?.metadata;

  const handleSubmit = (
    approval: VolunteerTimeApproval.Approved | VolunteerTimeApproval.Rejected,
    ids?: string[]
  ) => {
    const selectedIds = ids || selectedItems;

    if (!selectedIds?.length || anyStatusUpdating) {
      return;
    }

    setStatusUpdating(selectedIds.reduce((acc, id) => acc.set(id, true), new Map()));

    updateVolunteerTimes({
      variables: {
        ids: selectedIds,
        data: {
          approval,
        },
      },
    });
  };

  return (
    <>
      <Helmet title="Volunteer hours pending approval" />
      <PageHeader
        title="Volunteer Hours pending approval"
        onArrowClick={() => history.push('/volunteers')}
      />
      {user?.hasFeature(Features.VOLUNTEER_REWARD) && <Tabs selectedTab="VolunteerTimes" />}{' '}
      {volunteerTimesError ? (
        <Typography>There was an error loading Volunteer Hours list</Typography>
      ) : !volunteerTimes?.length && !volunteerTimesLoading && !volunteerTimesError ? (
        <Typography>You have no Volunteer Hours to approve!</Typography>
      ) : (
        <TableBox
          fixedBottom={
            <TablePagination
              rowsPerPageOptions={[50, 100, 200]}
              colSpan={6}
              count={volunteerTimesMetaData?.totalItems || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              // @NOTE-AC:
              // Keeping onChangePage (v4) and onPageChange (v5) for now
              // so it does not break when we update the version. And so
              // the type from MUI (that are wrong) stop throwing errors
              onPageChange={(_event, newPage): void => setPage(newPage)}
              onChangePage={(_event, newPage): void => setPage(newPage)}
              onRowsPerPageChange={(event): void => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              ActionsComponent={TablePaginationActions}
            />
          }
        >
          <Toolbar style={styles.tableHeader}>
            <Typography
              variant="h6"
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Employee Submissions
            </Typography>
            <Box style={styles.tableHeaderControls}>
              <Button
                style={{
                  ...styles.tableHeaderControl,
                  ...(!(selectedItems.length < 1 || anyStatusUpdating) && {
                    color: red,
                    border: `1px solid ${red}`,
                  }),
                }}
                disabled={selectedItems.length < 1 || anyStatusUpdating}
                onClick={() => handleSubmit(VolunteerTimeApproval.Rejected)}
              >
                {selectedItems.length === 1 ? (
                  <>Reject {selectedItems.length} submission</>
                ) : (
                  <>Reject {selectedItems.length} submissions</>
                )}
                <BlockIcon style={styles.tableHeaderControlIcon} />
              </Button>
              <Button
                style={styles.tableHeaderControl}
                disabled={selectedItems.length < 1 || anyStatusUpdating}
                onClick={() => handleSubmit(VolunteerTimeApproval.Approved)}
              >
                {selectedItems.length === 1 ? (
                  <>Approve {selectedItems.length} submission</>
                ) : (
                  <>Approve {selectedItems.length} submissions</>
                )}
                <CheckIcon style={styles.tableHeaderControlIcon} />
              </Button>
            </Box>
          </Toolbar>
          <Table size="small">
            {volunteerTimes?.length && (
              <VolunteerTimesTableHead
                volunteerTimes={volunteerTimes}
                statusUpdating={statusUpdating}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                anyStatusUpdating={anyStatusUpdating}
                setVolunteerTimeDetails={setVolunteerTimeDetails}
                setModalOpen={setModalOpen}
              />
            )}
            {volunteerTimesLoading && <LinearProgress />}
            <VolunteerTimesTableBody
              volunteerTimes={volunteerTimes}
              statusUpdating={statusUpdating}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              anyStatusUpdating={anyStatusUpdating}
              setVolunteerTimeDetails={setVolunteerTimeDetails}
              setModalOpen={setModalOpen}
            />
          </Table>
        </TableBox>
      )}
      {volunteerTimeDetails && (
        <VolunteerTimeDetails
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          volunteerTimeDetails={volunteerTimeDetails}
          handleSubmit={handleSubmit}
        />
      )}
    </>
  );
};

export default VolunteerTimesListPage;
